import { useAuth } from '@/auth/auth-provider'
import { canSeeData, isSuper, parsePrincipalFromUser } from '@/auth/principal'
import { CACHE_CONFIG } from '@/common/constants'
import { useDealershipGroups } from '@/hooks/useDashboard'
import { getDealershipsInGroup, getGroupDashboardStats, type DealershipBasics } from '@/services/dealership.api'
import { Dealership, DealershipEntity, DealershipGroup, DealershipType } from '@/types'
import { getIntervalIsoRange, type GroupInterval } from '@/utils/groupDashboardInterval'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

interface DealershipContextType {
  selectedEntity: DealershipEntity | null
  setSelectedEntity: (entity: DealershipEntity) => void
  entities: DealershipEntity[]
  isSuperUser: boolean
  currentType: DealershipType | null
  isLoading: boolean
  error: string | null
  /** Report window for group stats, Dealers list, and dealership dashboards (same ISO range → same API semantics) */
  groupDashboardInterval: GroupInterval
  setGroupDashboardInterval: (interval: GroupInterval) => void
  // Helper methods
  getDealershipsByGroup: (groupId: string) => Dealership[]
  getParentGroup: (dealershipId: string) => DealershipGroup | null
  findEntityById: (id: string, type?: DealershipType) => DealershipEntity | null
  refreshData: () => Promise<void>
}

// Default context value to prevent HMR errors
const defaultContextValue: DealershipContextType = {
  selectedEntity: null,
  setSelectedEntity: () => {},
  entities: [],
  isSuperUser: false,
  currentType: null,
  isLoading: true,
  error: null,
  groupDashboardInterval: 'this_week',
  setGroupDashboardInterval: () => {},
  getDealershipsByGroup: () => [],
  getParentGroup: () => null,
  findEntityById: () => null,
  refreshData: async () => {},
}

const DealershipContext = createContext<DealershipContextType>(defaultContextValue)

interface DealershipProviderProps {
  children: ReactNode
}

export function DealershipProvider({ children }: DealershipProviderProps) {
  const [selectedEntity, setSelectedEntity] = useState<DealershipEntity | null>(null)
  const [groupDashboardInterval, setGroupDashboardInterval] = useState<GroupInterval>('this_week')
  const queryClient = useQueryClient()
  const { user, isAuthenticated, isLoading: authLoading } = useAuth()
  const principal = useMemo(() => parsePrincipalFromUser(user), [user])
  const isSuperUser = useMemo(() => isSuper(principal), [principal])

  const { startDate: groupStatsStartIso, endDate: groupStatsEndIso } = useMemo(
    () => getIntervalIsoRange(groupDashboardInterval),
    [groupDashboardInterval],
  )

  const fetchDealershipContext = isAuthenticated && !authLoading

  // Fetch groups with React Query (cached automatically!)
  const { data: groupsData, isLoading: groupsLoading, error: groupsError } = useDealershipGroups(fetchDealershipContext)

  // Extract group IDs for parallel fetching
  const groupIds = groupsData?.map(g => g.groupId) || []

  // Fetch dealerships for each group in parallel (React Query will cache and deduplicate!)
  const dealershipsQueries = useQueries({
    queries: groupIds.map(groupId => ({
      queryKey: ['dealerships', groupId],
      queryFn: () => getDealershipsInGroup(groupId),
      staleTime: CACHE_CONFIG.STALE_TIME.DEALERSHIPS,
      gcTime: CACHE_CONFIG.GC_TIME.DEFAULT,
      retry: CACHE_CONFIG.RETRY_COUNT,
      refetchOnWindowFocus: false,
      enabled: !!groupId,
    })),
  })

  // Fetch group stats in parallel (same date window as Group Overview dashboard)
  const groupStatsQueries = useQueries({
    queries: groupIds.map(groupId => ({
      queryKey: ['group-stats', groupId, groupDashboardInterval],
      queryFn: () => getGroupDashboardStats(groupId, groupStatsStartIso, groupStatsEndIso),
      staleTime: CACHE_CONFIG.STALE_TIME.STATS,
      gcTime: CACHE_CONFIG.GC_TIME.DEFAULT,
      retry: CACHE_CONFIG.RETRY_COUNT,
      refetchOnWindowFocus: false,
      enabled: !!groupId,
    })),
  })

  // Transform API data into entity tree, then scope to JWT (matrix: selector = Scoped for non-super)
  const rawGroups = useMemo(() => {
    if (!groupsData) return []

    return groupsData.map((apiGroup, groupIndex) => {
      const dealershipsData = dealershipsQueries[groupIndex]?.data || []
      const groupStats = groupStatsQueries[groupIndex]?.data

      // Create dealerships with stats
      const dealerships: Dealership[] = dealershipsData.map((d: DealershipBasics) => {
        const dealerBreakdown = groupStats?.dealerships?.find(ds => ds.dealershipId === d.dealershipId)

        return {
          id: d.dealershipId,
          name: d.configurationData.storeName || `Dealership ${d.dealershipId}`,
          type: 'dealership' as const,
          groupId: apiGroup.groupId,
          location: d.configurationData.address
            ? `${d.configurationData.address.city || ''}, ${d.configurationData.address.state || ''}`.trim()
            : 'Unknown Location',
          phone: d.configurationData.primaryPhone,
          email: d.configurationData.email,
          status: 'active',
          totalConversations: dealerBreakdown?.totalConversations ?? 0,
          totalAppointments: dealerBreakdown?.totalAppointments ?? 0,
          upcomingAppointments: dealerBreakdown?.upcomingAppointments ?? 0,
        }
      })

      const group: DealershipGroup = {
        id: apiGroup.groupId,
        name: apiGroup.configurationData.groupName || `Group ${apiGroup.groupId}`,
        type: 'group',
        dealershipCount: dealerships.length,
        totalLeads: groupStats?.totalLeads ?? 0,
        totalConversations: groupStats?.totalConversations ?? 0,
        totalRevenue: 0,
        dealerships,
      }

      return group
    })
  }, [groupsData, dealershipsQueries, groupStatsQueries])

  const entities = useMemo(() => (canSeeData(principal) ? rawGroups : []), [rawGroups, principal])

  // Compute loading and error states (check all queries)
  const isLoading = groupsLoading || dealershipsQueries.some(q => q.isLoading) || groupStatsQueries.some(q => q.isLoading)

  // Check if dealerships data is fully loaded (critical for proper entity restoration)
  const dealershipsLoaded = !groupsLoading && dealershipsQueries.every(q => !q.isLoading)

  // Collect errors from all queries
  const errors: string[] = []
  if (groupsError) errors.push(`Groups: ${(groupsError as Error).message}`)
  dealershipsQueries.forEach((q, i) => {
    if (q.error) errors.push(`Dealerships (${groupIds[i]}): ${(q.error as Error).message}`)
  })
  groupStatsQueries.forEach((q, i) => {
    if (q.error) errors.push(`Group Stats (${groupIds[i]}): ${(q.error as Error).message}`)
  })

  const error = errors.length > 0 ? errors.join('; ') : null

  useEffect(() => {
    if (error) {
      console.error('DealershipContext errors:', error)
    }
  }, [error])

  // Restore selected entity from localStorage when entities load
  useEffect(() => {
    // Skip if no entities yet
    if (entities.length === 0) return

    // CRITICAL: Wait for dealerships to be loaded before restoring
    // This prevents restoration from happening when groups exist but dealerships array is still empty
    if (!dealershipsLoaded) return

    // Skip if already initialized
    if (selectedEntity !== null) return

    const saved = localStorage.getItem('selectedDealershipEntity')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        // Use the saved type to disambiguate if IDs overlap
        const found = findEntityByIdInArray(parsed.id, entities, parsed.type)
        if (found) {
          setSelectedEntity(found)
        } else {
          setSelectedEntity(entities[0])
        }
      } catch (error) {
        console.error('Failed to parse saved entity, falling back to first entity:', error)
        setSelectedEntity(entities[0])
      }
    } else {
      setSelectedEntity(entities[0])
    }
  }, [entities.length, selectedEntity, dealershipsLoaded]) // Now also depends on dealershipsLoaded

  /** If scope or data shrinks, clear a selection that no longer appears in the scoped tree. */
  useEffect(() => {
    if (entities.length === 0) {
      if (selectedEntity !== null) {
        setSelectedEntity(null)
        localStorage.removeItem('selectedDealershipEntity')
      }
      return
    }
    if (!selectedEntity) return
    const found = findEntityByIdInArray(selectedEntity.id, entities, selectedEntity.type)
    if (!found) {
      setSelectedEntity(entities[0])
    }
  }, [entities, selectedEntity])

  // Keep selectedEntity in sync with the latest entity data
  // This ensures that if dealerships data loads after initial entity selection,
  // we update selectedEntity to reference the latest entity with populated data
  useEffect(() => {
    if (!selectedEntity || entities.length === 0) return

    // Find the latest version of the selected entity in the entities array
    const latestEntity = findEntityByIdInArray(selectedEntity.id, entities)

    if (!latestEntity) {
      setSelectedEntity(entities[0] ?? null)
      return
    }

    // Check if the entity has more data than before (e.g., dealerships loaded)
    // Compare meaningful data instead of just reference equality to avoid infinite loops
    if (latestEntity.type === 'group') {
      const currentGroup = selectedEntity.type === 'group' ? selectedEntity : null
      const latestGroup = latestEntity as DealershipGroup

      // Only update if dealerships count changed (data was loaded)
      if (currentGroup && latestGroup.dealerships.length !== currentGroup.dealerships.length) {
        setSelectedEntity(latestEntity)
      }
    }
  }, [entities, selectedEntity]) // Include selectedEntity to properly track changes

  // Save selected entity to localStorage
  useEffect(() => {
    if (selectedEntity) {
      localStorage.setItem('selectedDealershipEntity', JSON.stringify({ id: selectedEntity.id, type: selectedEntity.type }))
    }
  }, [selectedEntity])

  // Wrap helper functions in useCallback to prevent unnecessary re-renders
  const getDealershipsByGroup = useCallback(
    (groupId: string): Dealership[] => {
      const group = entities.find(e => e.id === groupId && e.type === 'group') as DealershipGroup
      return group?.dealerships || []
    },
    [entities],
  )

  const getParentGroup = useCallback(
    (dealershipId: string): DealershipGroup | null => {
      for (const entity of entities) {
        if (entity.type === 'group') {
          const group = entity as DealershipGroup
          if (group.dealerships.some(d => d.id === dealershipId)) {
            return group
          }
        }
      }
      return null
    },
    [entities],
  )

  const findEntityById = useCallback(
    (id: string, type?: DealershipType): DealershipEntity | null => {
      return findEntityByIdInArray(id, entities, type)
    },
    [entities],
  )

  const findEntityByIdInArray = (
    id: string,
    entitiesArray: DealershipEntity[],
    type?: DealershipType,
  ): DealershipEntity | null => {
    // If type is specified, search only that type to avoid ambiguity
    if (type === 'group') {
      const group = entitiesArray.find(e => e.id === id && e.type === 'group')
      return group || null
    }

    if (type === 'dealership') {
      for (const entity of entitiesArray) {
        if (entity.type === 'group') {
          const g = entity as DealershipGroup
          const dealer = g.dealerships.find(d => d.id === id)
          if (dealer) return dealer
        }
      }
      return null
    }

    // No type specified: search both, but this can be ambiguous if IDs overlap
    // Check dealerships first (more specific entities)
    for (const entity of entitiesArray) {
      if (entity.type === 'group') {
        const g = entity as DealershipGroup
        const dealer = g.dealerships.find(d => d.id === id)
        if (dealer) return dealer
      }
    }

    // Then check if it's a group
    const group = entitiesArray.find(e => e.id === id && e.type === 'group')
    if (group) return group

    return null
  }

  // Refresh data by invalidating React Query cache (wrapped in useCallback for stability)
  const refreshData = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['dealership-groups'] })
    await queryClient.invalidateQueries({ queryKey: ['dealerships'] })
    await queryClient.invalidateQueries({ queryKey: ['group-stats'], exact: false })
    await queryClient.invalidateQueries({ queryKey: ['dealership-stats'] })
  }, [queryClient])

  return (
    <DealershipContext.Provider
      value={{
        selectedEntity,
        setSelectedEntity,
        entities,
        isSuperUser,
        currentType: selectedEntity?.type || null,
        isLoading,
        error,
        groupDashboardInterval,
        setGroupDashboardInterval,
        getDealershipsByGroup,
        getParentGroup,
        findEntityById,
        refreshData,
      }}
    >
      {children}
    </DealershipContext.Provider>
  )
}

export function useDealership() {
  const context = useContext(DealershipContext)
  return context
}
